<!-- src/components/Common/CustomerSearchAutocomplete.vue -->
<template>
  <div ref="containerRef" class="relative w-full max-w-xs" v-click-outside="closeDropdown">
    <div class="relative">
      <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <!-- Show spinner while loading, search icon otherwise -->
        <span
          v-if="isLoadingCustomers"
          class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-brand-blue"
        />
        <Search v-else class="h-4 w-4 text-gray-400" aria-hidden="true" />
      </div>
      <input
        type="text"
        :id="inputId"
        v-model="searchQuery"
        @focus="handleFocus"
        @keydown.down.prevent="navigateResults('down')"
        @keydown.up.prevent="navigateResults('up')"
        @keydown.enter.prevent="selectOnEnter"
        :placeholder="isLoadingCustomers ? 'Loading...' : placeholder"
        :aria-label="ariaLabel"
        :maxlength="maxlength"
        class="block w-full rounded-md border-0 bg-gray-100 py-1.5 pl-9 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 transition placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-blue sm:text-sm sm:leading-6"
      />
    </div>
    <!-- Select mode, dropdown closed: say what the typed text is, so text that was never picked
         never passes for a choice (docs/bugs/referrer_match_silent_no_op_BUG.md). -->
    <p
      v-if="selectHint"
      data-test="select-hint"
      role="status"
      :class="['mt-1 text-xs', selectHint.noMatch ? 'text-amber-700' : 'text-gray-500']"
    >
      {{ selectHint.text }}
    </p>
    <!-- suggestFor: the customers matching text the caller already has, e.g. the referrer a customer entered. -->
    <div v-if="suggestions" data-test="suggestions" class="mt-2">
      <p v-if="suggestions.loading" class="flex items-center gap-2 text-xs text-gray-500">
        <span
          class="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-brand-blue"
        />
        Looking for customers matching {{ suggestions.label }}…
      </p>
      <template v-else-if="suggestions.items.length">
        <p class="mb-1 text-xs font-medium text-gray-700">
          Customers matching {{ suggestions.label }}
        </p>
        <ul class="divide-y divide-gray-100 rounded-md border border-gray-200">
          <li v-for="customer in suggestions.items" :key="customer.id">
            <button
              type="button"
              class="flex w-full flex-col px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
              @click="selectCustomer(customer)"
            >
              <span class="flex min-w-0 items-center gap-2 text-sm font-medium text-gray-900">
                <span class="truncate">{{ customer.name }}</span>
                <span
                  v-if="customer.finished"
                  :class="[
                    'shrink-0 text-[10px]',
                    customer.statusLabel === 'Canceled' ? 'status-pill-gray' : 'status-pill-green',
                  ]"
                >
                  {{ customer.statusLabel }}
                </span>
              </span>
              <span v-if="customer.address" class="truncate text-xs text-gray-500">
                {{ customer.address }}
              </span>
              <span v-if="customer.email || customer.phone" class="truncate text-xs text-gray-500">
                {{ [customer.email, customer.phone].filter(Boolean).join(' · ') }}
              </span>
            </button>
          </li>
        </ul>
      </template>
      <p v-else class="text-xs text-amber-700">
        No customer matches {{ suggestions.label }}. Search by name, phone or email above.
      </p>
    </div>
    <Teleport to="body">
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <div
          v-if="showResults && dropdownStyle"
          class="fixed z-[9999] max-h-80 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
          :style="dropdownStyle"
        >
          <ul>
            <!-- Loading state -->
            <li
              v-if="isLoadingCustomers || !directorySettled"
              class="relative cursor-default select-none px-4 py-3 text-gray-500"
            >
              <div class="flex items-center gap-2">
                <span
                  class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-brand-blue"
                />
                <span>Loading customers...</span>
              </div>
            </li>
            <!-- No results -->
            <li
              v-else-if="!filteredCustomers.length"
              class="relative cursor-default select-none px-4 py-2 text-gray-700"
            >
              No customer matches “{{ searchQuery.trim() }}”.
            </li>
            <!-- Results -->
            <li
              v-for="(customer, index) in filteredCustomers"
              :key="customer.id"
              @click="selectCustomer(customer)"
              :class="[
                'relative cursor-pointer select-none py-2 pl-3 pr-9',
                {
                  'bg-brand-blue text-white': activeIndex === index,
                  'text-gray-900': activeIndex !== index,
                },
              ]"
            >
              <div class="flex flex-col">
                <span class="flex min-w-0 items-center gap-2">
                  <span :class="['block truncate', { 'font-semibold': activeIndex === index }]">
                    {{ customer.name }}
                  </span>
                  <!-- A finished customer from the directory: open it to Reactivate / UnCancel. -->
                  <span
                    v-if="customer.finished"
                    :class="[
                      'shrink-0 text-[10px]',
                      customer.statusLabel === 'Canceled'
                        ? 'status-pill-gray'
                        : 'status-pill-green',
                    ]"
                  >
                    {{ customer.statusLabel }}
                  </span>
                </span>
                <span
                  v-if="customer.address"
                  :class="[
                    'block truncate text-xs',
                    {
                      'text-indigo-200': activeIndex === index,
                      'text-gray-500': activeIndex !== index,
                    },
                  ]"
                >
                  {{ customer.address }}
                </span>
                <span
                  v-if="customer.phone"
                  :class="[
                    'block truncate text-xs',
                    {
                      'text-indigo-200': activeIndex === index,
                      'text-gray-500': activeIndex !== index,
                    },
                  ]"
                >
                  {{ customer.phone }}
                </span>
              </div>
              <span
                v-if="activeIndex === index"
                :class="[
                  'absolute inset-y-0 right-0 flex items-center pr-4',
                  { 'text-white': activeIndex === index },
                ]"
              >
                <Check class="h-5 w-5" aria-hidden="true" />
              </span>
            </li>
          </ul>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useDataStore } from '@/stores/dataStore'
import { useLeadStore } from '@/stores/leadStore'
import { useCustomerDirectoryStore } from '@/stores/customerDirectoryStore'
import Fuse from 'fuse.js'
import { vClickOutside } from '@/directives/clickOutside'
import { Search, Check } from 'lucide-vue-next'
import { derivePhoneLast10 } from '@/utils/formatters'
import { customerAddressLine } from '@/utils/customerAddress'

const dataStore = useDataStore()
const leadStore = useLeadStore()
const directoryStore = useCustomerDirectoryStore()
const router = useRouter()

const searchQuery = ref('')
const isFocused = ref(false)
const activeIndex = ref(-1)
// The name this box last put in the input for a pick (select mode). Text that differs was typed, not picked.
const pickedName = ref('')
const emit = defineEmits(['customer-selected', 'update:modelValue'])
const containerRef = ref(null)
const dropdownPosition = ref({ top: 0, left: 0, width: 0 })
let fuse = null
// Bumped whenever the index is built or refilled. The index itself is not reactive, so a search made before the
// customer lists loaded would otherwise stay empty until the next keystroke.
const indexVersion = ref(0)

// Calculate dropdown position based on input location
const dropdownStyle = computed(() => {
  if (!dropdownPosition.value.width) return null
  return {
    top: `${dropdownPosition.value.top}px`,
    left: `${dropdownPosition.value.left}px`,
    width: `${dropdownPosition.value.width}px`,
  }
})

// Update position when dropdown opens
const updateDropdownPosition = () => {
  if (containerRef.value) {
    const rect = containerRef.value.getBoundingClientRect()
    dropdownPosition.value = {
      top: rect.bottom + 4, // 4px gap
      left: rect.left,
      width: rect.width,
    }
  }
}

const props = defineProps({
  // Optional free-text model for scenario builders; selection-only pickers keep their existing behavior.
  modelValue: { type: String, default: undefined },
  inputId: { type: String, default: undefined },
  allowCustom: { type: Boolean, default: false },
  placeholder: { type: String, default: 'Search customers...' },
  ariaLabel: { type: String, default: 'Search customers' },
  maxlength: { type: Number, default: undefined },
  mode: {
    type: String,
    default: 'navigate', // 'navigate' or 'select'
  },
  // Also search FINISHED customers (completed + canceled) through the customerDirectory index
  // (docs/features/FINISHED_CUSTOMER_SEARCH_FEATURE.md). The top-bar search opts in, and so do the two
  // referral-link pickers (Resolve Referrer Match, Pending Referrals), because linking a referral credits a
  // customer and doesn't work them (Erik, 2026-09-26). Every picker that WORKS a customer stays active + lead,
  // so a finished customer is reactivated before it is worked (Erik, 2026-09-23).
  includeFinished: {
    type: Boolean,
    default: false,
  },
  // Select mode: text the caller already has about the customer to pick (the referrer a customer entered, a
  // referral's email / phone / name). The customers matching it are listed under the box until one is picked.
  suggestFor: {
    type: [String, Array],
    default: '',
  },
})

watch(
  () => props.modelValue,
  value => {
    if (value !== undefined && value !== searchQuery.value) {
      searchQuery.value = value
      pickedName.value = ''
    }
  },
  { immediate: true }
)
watch(searchQuery, value => {
  if (props.modelValue !== undefined && value !== props.modelValue) emit('update:modelValue', value)
})

// Reactive loading state - both stores loading
const isLoadingCustomers = computed(() => dataStore.isLoading || leadStore.isLoading)

// The directory has answered (or refused this session). Only a box that asks for finished customers waits on it.
const directorySettled = ref(!props.includeFinished)

// Every list this box searches has delivered at least once. Until then an empty result means "not loaded",
// never "no match".
const hasLoaded = computed(
  () => dataStore.hasInitialData && leadStore.hasInitialData && directorySettled.value
)

// Merge customers from dataStore (active pipeline) + leadStore (lead pipeline)
// Maps V2 nested paths to flat searchable fields for Fuse.js
const allCustomers = computed(() => {
  const activeCustomers = dataStore.customers || []
  const leads = leadStore.leads || []

  // Use a Map to deduplicate by ID (in case of any overlap)
  const merged = new Map()
  activeCustomers.forEach(c => merged.set(c.id, c))
  leads.forEach(c => merged.set(c.id, c))

  const live = Array.from(merged.values()).map(c => {
    const phone = c.contact?.phone || ''
    return {
      ...c,
      name: `${c.contact?.firstName || ''} ${c.contact?.lastName || ''}`.trim() || c.id,
      address: customerAddressLine(c),
      email: c.contact?.email || '',
      // Alternate contacts: the same arrays the backend matches inbound email, calls and referrer answers against
      // (functions-shared/utils/customerLookup.js). Each holds the primary too.
      emails: Array.isArray(c.contact?.emails) ? c.contact.emails : [],
      phone,
      // Digits-only last-10 form so a query like "5551234" or
      // "3125551234" matches against the same field even when the
      // stored phone is formatted as "(312) 555-1234" or "+1...".
      phoneLast10: derivePhoneLast10(phone) || '',
      phonesLast10: Array.isArray(c.contact?.phonesLast10) ? c.contact.phonesLast10 : [],
      // The P-number's canonical home is product.elevateProjectId (the root copy is legacy).
      elevateProjectId: c.product?.elevateProjectId || c.elevateProjectId || '',
    }
  })

  // Finished customers from the index, after the live ones; a live document always wins.
  if (!props.includeFinished || !directoryStore.hasLoaded) return live
  return live.concat(directoryStore.entries.filter(e => !merged.has(e.id)))
})

// Initialize/update Fuse when customers change
watch(
  allCustomers,
  newCustomers => {
    if (newCustomers.length > 0) {
      if (!fuse) {
        fuse = new Fuse(newCustomers, {
          keys: [
            'name',
            'address',
            'email',
            'emails',
            'phone',
            'phoneLast10',
            'phonesLast10',
            'id',
            'elevateProjectId',
          ],
          includeScore: true,
          threshold: 0.4,
        })
      } else {
        fuse.setCollection(newCustomers)
      }
      indexVersion.value += 1
    }
  },
  { immediate: true }
)

/** Subscribe the stores this box searches (each call is idempotent). */
const ensureCustomersLoaded = () => {
  // Lazy load: subscribe to stores when user first interacts
  if (!leadStore.hasInitialData) {
    leadStore.fetchLeads()
  }
  if (!dataStore.hasInitialData) {
    dataStore.fetchCustomers()
  }
  // Four small documents, once per session, and only when this box asks for finished customers
  // (the store refuses without the flag or full customer visibility).
  if (props.includeFinished) {
    Promise.resolve(directoryStore.subscribe()).finally(() => {
      directorySettled.value = true
    })
  }
}

/**
 * Handle focus - no lazy loading needed since dataStore and leadStore
 * are already subscribed via App.vue / dashboard pages
 */
const handleFocus = async () => {
  isFocused.value = true
  updateDropdownPosition()
  ensureCustomersLoaded()
}

/** The customers matching `text`, best first. */
const searchCustomers = (text, limit = 10) => {
  void indexVersion.value // re-run when the index is (re)built
  const query = String(text || '').trim()
  if (!query || !fuse) return []
  return fuse
    .search(query)
    .map(result => result.item)
    .slice(0, limit)
}

const filteredCustomers = computed(() => searchCustomers(searchQuery.value))

const showResults = computed(() => isFocused.value && searchQuery.value.trim().length > 0)

const selectHint = computed(() => {
  if (props.allowCustom) return null
  if (props.mode !== 'select' || showResults.value) return null
  const query = searchQuery.value.trim()
  if (!query || query === pickedName.value || !hasLoaded.value) return null
  if (!filteredCustomers.value.length) {
    return {
      noMatch: true,
      text: `No customer matches “${query}”. Try their name, phone or email.`,
    }
  }
  return {
    noMatch: false,
    text: pickedName.value
      ? 'To change the selection, click the box and choose a customer from the list.'
      : 'Nothing is selected yet. Click the box and choose a customer from the list.',
  }
})

const suggestionHints = computed(() =>
  (Array.isArray(props.suggestFor) ? props.suggestFor : [props.suggestFor])
    .map(hint => String(hint || '').trim())
    .filter(Boolean)
)

watch(
  suggestionHints,
  hints => {
    if (props.mode === 'select' && hints.length) ensureCustomersLoaded()
  },
  { immediate: true }
)

const suggestions = computed(() => {
  if (props.mode !== 'select' || !suggestionHints.value.length) return null
  // Suggestions answer the caller's text; once something is picked or typed, the box speaks for itself.
  if (pickedName.value || searchQuery.value.trim()) return null
  const label = suggestionHints.value.map(hint => `“${hint}”`).join(' or ')
  if (!hasLoaded.value) return { label, loading: true, items: [] }
  const seen = new Set()
  const items = []
  for (const hint of suggestionHints.value) {
    for (const customer of searchCustomers(hint, 5)) {
      if (!seen.has(customer.id)) {
        seen.add(customer.id)
        items.push(customer)
      }
    }
  }
  return { label, loading: false, items: items.slice(0, 5) }
})

const closeDropdown = () => {
  isFocused.value = false
  activeIndex.value = -1
}

const navigateResults = direction => {
  if (filteredCustomers.value.length === 0) return

  if (direction === 'down') {
    activeIndex.value = (activeIndex.value + 1) % filteredCustomers.value.length
  } else if (direction === 'up') {
    activeIndex.value =
      (activeIndex.value - 1 + filteredCustomers.value.length) % filteredCustomers.value.length
  }
}

// Enter picks the highlighted row, or the only row when there is exactly one.
const selectOnEnter = () => {
  if (activeIndex.value >= 0) {
    selectCustomer(filteredCustomers.value[activeIndex.value])
  } else if (filteredCustomers.value.length === 1) {
    selectCustomer(filteredCustomers.value[0])
  }
}

const selectCustomer = (customer = null) => {
  const selected =
    customer || (activeIndex.value >= 0 ? filteredCustomers.value[activeIndex.value] : null)

  if (selected) {
    if (props.mode === 'navigate') {
      // SPA navigation — keeps any in-progress voice call alive (full
      // page navigation would fire beforeunload and drop the call).
      router.push({ name: 'CustomerCommandCenter', params: { id: selected.id } })
    } else {
      emit('customer-selected', selected)
      searchQuery.value = selected.name
      pickedName.value = selected.name
    }
    closeDropdown()
  }
}
</script>
